import { useEffect, useRef, useState } from 'react';

import { Modal, Space, Typography } from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import type { ClinicOfferResponse } from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { offersApi } from '@/features/settings/api';
import { useClinicOffers } from '@/features/settings/hooks';
import { useProactiveSlot } from '@/guidance';

import { OFFER_COPY } from './copy';
import { OfferCopy } from './offer-copy';
import { OfferCountdown } from './offer-countdown';
import { useDeadline } from './use-deadline';

const { Text } = Typography;

/**
 * The one-time offer popup, for a spotlight offer the console marked `modal`
 * and the clinic has not been shown it. It spends the guidance proactive
 * budget like a tour does, so it never lands on top of one; `seen(…,'modal')`
 * records it server-side so it appears once, on any device.
 */
export function OfferModal({
  offer,
  onClose,
}: {
  offer: ClinicOfferResponse | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const wanted = Boolean(offer && offer.modal && !offer.modalShown);
  const granted = useProactiveSlot(wanted);
  const { msLeft } = useDeadline(offer?.expiresAt ?? null);
  const marked = useRef<string | null>(null);
  const [closed, setClosed] = useState(false);
  const open = Boolean(offer) && granted && !closed && (msLeft === null || msLeft > 0);
  const close = (): void => {
    setClosed(true);
    onClose();
  };

  useEffect(() => {
    if (!open || !offer || marked.current === offer.grantId) return;
    marked.current = offer.grantId;
    offersApi.seen(offer.grantId, 'modal').catch(() => undefined);
  }, [open, offer]);

  if (!offer) return null;
  const figures = { ...offer };

  return (
    <Modal
      open={open}
      centered
      title={<OfferCopy offer={figures} text={offer.headline} />}
      okText={OFFER_COPY.claim}
      cancelText={OFFER_COPY.notNow}
      onOk={() => {
        close();
        void navigate(`/settings/subscription?offer=${encodeURIComponent(offer.grantId)}`);
      }}
      onCancel={close}
    >
      <Space orientation="vertical" size={8}>
        {offer.body ? (
          <Text>
            <OfferCopy offer={figures} text={offer.body} />
          </Text>
        ) : null}
        <Text type="secondary">
          <OfferCountdown expiresAt={offer.expiresAt} />
        </Text>
      </Space>
    </Modal>
  );
}

/** Mounted once in the shell: reads the spotlight and shows its modal at most once. */
export function OfferModalHost() {
  const { impersonatedBy } = useAuth();
  const offers = useClinicOffers({ enabled: !impersonatedBy });
  return <OfferModal offer={offers.data?.spotlight ?? null} onClose={() => undefined} />;
}
